// Detail (§6.6) — neighbourhood passes, mirrored exactly by cpuPipeline.cpuDetail.
//  1. TO_LAB: linear P3 → OkLab (+alpha), once per pixel.
//  2. NR: joint bilateral 7×7 on L (spatial σ 1.5 px, range σ from nrLuma) + 9×9 Gaussian (σ 2 px) of a/b
//     mixed by nrColor; back to linear P3.
//  3. SHARP_LUMA → (DETAIL_BLUR ×2) → SHARP_COMBINE: unsharp mask on encoded luma, times a Sobel edge mask whose
//     thresholds rise with sharpMasking.
// Inputs may be premultiplied (export/100% region path) or straight (preview); uPremul says which, and the
// output uses the same convention. Spatial radii are multiplied by uScale (preview below 100%).
import { COLOR_FNS, HEADER, OKLAB_FNS } from './common';

const FETCH = `
uniform sampler2D uSrc;
uniform int uPremul;
vec4 fetchStraight(ivec2 p) {
  ivec2 sz = textureSize(uSrc, 0);
  vec4 c = texelFetch(uSrc, clamp(p, ivec2(0), sz - 1), 0);
  if (uPremul == 1) c.rgb = c.a > 1e-6 ? c.rgb / c.a : vec3(0.0);
  return c;
}
vec4 outConv(vec3 rgb, float a) { return uPremul == 1 ? vec4(rgb * a, a) : vec4(rgb, a); }
`;

export const TO_LAB = `${HEADER}${COLOR_FNS}${OKLAB_FNS}${FETCH}
out vec4 o;
void main() {
  vec4 c = fetchStraight(ivec2(gl_FragCoord.xy));
  o = vec4(p3ToOklab(c.rgb), c.a);
}`;

export const NR = `${HEADER}${COLOR_FNS}${OKLAB_FNS}
uniform sampler2D uLab;
uniform int uPremul;
uniform float uLuma;       // 0..100
uniform float uColor;      // 0..100
uniform float uSigmaR;     // range sigma in OkLab L
uniform float uScale;
out vec4 o;
vec4 lab(ivec2 p) { ivec2 sz = textureSize(uLab, 0); return texelFetch(uLab, clamp(p, ivec2(0), sz - 1), 0); }
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 c0 = lab(p);
  float ss = 1.5 * uScale, sa = 2.0 * uScale;
  float wsum = 0.0, Lacc = 0.0, gsum = 0.0;
  vec2 ab = vec2(0.0);
  for (int j = -4; j <= 4; j++) {
    for (int i = -4; i <= 4; i++) {
      vec4 q = lab(p + ivec2(i, j));
      float g = exp(-float(i * i + j * j) / (2.0 * sa * sa));
      gsum += g;
      ab += g * q.yz;
      if (abs(i) <= 3 && abs(j) <= 3) {
        float dl = q.x - c0.x;
        float w = exp(-float(i * i + j * j) / (2.0 * ss * ss)) * exp(-(dl * dl) / (2.0 * uSigmaR * uSigmaR));
        wsum += w;
        Lacc += w * q.x;
      }
    }
  }
  float L = uLuma > 0.0 ? Lacc / wsum : c0.x;
  vec2 ab2 = c0.yz + (ab / gsum - c0.yz) * (uColor / 100.0);
  vec3 rgb = oklabToP3(vec3(L, ab2));
  o = uPremul == 1 ? vec4(rgb * c0.w, c0.w) : vec4(rgb, c0.w);
}`;

export const SHARP_LUMA = `${HEADER}${COLOR_FNS}${FETCH}
out vec4 o;
void main() {
  vec4 c = fetchStraight(ivec2(gl_FragCoord.xy));
  o = vec4(lumaP3(encodeSigned(c.rgb)), 0.0, 0.0, 1.0);
}`;

export const SHARP_COMBINE = `${HEADER}${COLOR_FNS}${FETCH}
uniform sampler2D uLuma;
uniform sampler2D uBlur;
uniform float uAmount;     // 0..150
uniform float uMasking;    // 0..100
uniform vec2 uThresh;      // edge mask smoothstep thresholds
out vec4 o;
float v(ivec2 p) { ivec2 sz = textureSize(uLuma, 0); return texelFetch(uLuma, clamp(p, ivec2(0), sz - 1), 0).r; }
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 c = fetchStraight(p);
  float mask = 1.0;
  if (uMasking > 0.0) {
    float gx = (v(p + ivec2(1, -1)) + 2.0 * v(p + ivec2(1, 0)) + v(p + ivec2(1, 1)) - v(p + ivec2(-1, -1)) - 2.0 * v(p + ivec2(-1, 0)) - v(p + ivec2(-1, 1))) / 8.0;
    float gy = (v(p + ivec2(-1, 1)) + 2.0 * v(p + ivec2(0, 1)) + v(p + ivec2(1, 1)) - v(p + ivec2(-1, -1)) - 2.0 * v(p + ivec2(0, -1)) - v(p + ivec2(1, -1))) / 8.0;
    float t = clamp((length(vec2(gx, gy)) - uThresh.x) / (uThresh.y - uThresh.x), 0.0, 1.0);
    mask = t * t * (3.0 - 2.0 * t);
  }
  float blur = texelFetch(uBlur, p, 0).r;
  float delta = uAmount / 100.0 * (v(p) - blur) * mask;
  vec3 rgb = decodeSigned(encodeSigned(c.rgb) + delta);
  o = outConv(rgb, c.a);
}`;

/** Detail: separable Gaussian on the R channel (clamped edges), sigma uSigma, radius uR (≤ 12). */
export const DETAIL_BLUR = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uDir;
uniform float uSigma;
uniform int uR;
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  ivec2 sz = textureSize(uSrc, 0);
  float acc = 0.0, ks = 0.0;
  for (int i = -12; i <= 12; i++) {
    if (i < -uR || i > uR) continue;
    float w = exp(-0.5 * float(i * i) / (uSigma * uSigma));
    acc += w * texelFetch(uSrc, clamp(p + ivec2(uDir) * i, ivec2(0), sz - 1), 0).r;
    ks += w;
  }
  o = vec4(acc / ks, 0.0, 0.0, 1.0);
}`;
